import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Bell, CaretRight, CheckCircle, Info, Warning, XCircle } from '@phosphor-icons/react';

import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
  type Notification,
  type NotificationSeverity,
} from '../api/notify';
import { useNotificationsStream } from '@/hooks/useNotificationsStream';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { Button } from '@/components/ui/button';
import { resolveNotificationLink } from '@/lib/notificationLink';
import { cn } from '@/lib/utils';

// Prefix for synthetic notification rows backed by /announcements.json
// entries. The double-underscore prefix can't collide with a real UUID, so
// the row dispatcher recognises these and routes them to the announcements
// markSeen handler + action_url instead of the usual server mark-read +
// deep-link flow.
const ANNOUNCEMENT_ID_PREFIX = '__ann_';

// NotificationBell is the bell icon + popover dropdown that lives in the
// top-right corner of DashboardShell, immediately to the left of the
// settings cog.
//
// Real-time delivery is via Server-Sent Events: useNotificationsStream
// opens /notifications/stream and pushes new rows into the React-Query
// cache, so the badge updates the instant the server fires. Polling
// stays as a low-frequency safety net — once a minute when the bell is
// closed, every 30s when open — to catch anything the stream might have
// missed (e.g. during the SPA's first render before the stream opens, or
// across a multi-second reconnect gap on a flaky network).
//
// Stream architecture: see internal/notify/{hub,listen}.go and the
// /stream handler in internal/api/notify.go.

export interface NotificationBellProps {
  slug: string;
}

const POLL_OPEN_MS = 30_000;
const POLL_CLOSED_MS = 60_000;

export function NotificationBell({ slug }: NotificationBellProps) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const currentSlug = slug || params.slug;

  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Click-outside + escape close the popover. The native <dialog> would
  // give us escape-handling for free but we want the bell to behave like
  // a popover (no backdrop, doesn't trap focus aggressively), so we wire
  // them manually.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const query = useQuery({
    queryKey: ['notifications', slug],
    queryFn: () => listNotifications(token, slug, { limit: 20 }),
    enabled: !!token && !!slug,
    refetchInterval: open ? POLL_OPEN_MS : POLL_CLOSED_MS,
    // Re-fetch on focus too so a user who comes back from another tab sees
    // the badge update immediately without waiting for the next poll tick.
    refetchOnWindowFocus: true,
  });

  // Real-time stream. Pushes server-fired notifications into the same
  // ['notifications', slug] query cache the bell renders from, so new
  // rows appear instantly without waiting for the next poll.
  useNotificationsStream(slug, token);

  const markRead = useMutation({
    mutationFn: (ids: string[]) => markNotificationsRead(token, slug, ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', slug] });
    },
  });
  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(token, slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', slug] });
    },
  });

  // Platform announcements (release notes, downtime warnings, etc). Pulled
  // from /announcements.json and surfaced as synthetic rows so they appear
  // alongside server-emitted notifications. Read state lives in
  // localStorage — see useAnnouncements for the lifecycle.
  const { unseen: announcements, markSeen, markAllSeen } = useAnnouncements();

  const items: Notification[] = useMemo(() => {
    const base = query.data?.items ?? [];
    if (announcements.length === 0) return base;
    const synthetic: Notification[] = announcements.map((a) => ({
      id: `${ANNOUNCEMENT_ID_PREFIX}${a.id}`,
      kind: 'announcement',
      severity: a.severity,
      title: t(`announcements.${a.id}.title`, {
        defaultValue: t('notifications.kind.announcement.title'),
      }),
      body: t(`announcements.${a.id}.body`, {
        defaultValue: t('notifications.kind.announcement.body'),
      }),
      ...(a.action_url ? { action_url: a.action_url } : {}),
      created_at: a.created_at,
    }));
    return [...synthetic, ...base];
  }, [query.data?.items, announcements, t]);

  const unread = (query.data?.unread ?? 0) + announcements.length;
  const dateFmt = new Intl.DateTimeFormat(i18n.language || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: false,
  });

  // Backend action URLs are often org-relative ("/pulse", "/assistant",
  // "/orgs/_/billing") — the convention is that the SPA splices in the org
  // slug from its router context. resolveNotificationLink does that and
  // returns null when the URL can't be resolved, so we never navigate to a
  // path that matches no route.
  const link = (n: Notification) => resolveNotificationLink(n.action_url, currentSlug);

  // Follow a resolved action link: client-side navigation for SPA paths (no
  // full reload), a real navigation only for absolute/off-app URLs.
  const follow = (target: ReturnType<typeof link>) => {
    if (!target) return;
    if (target.external) {
      window.location.href = target.href;
      return;
    }
    navigate(target.href);
  };

  // Mirrors the onClick branches below: a row "has an action" when clicking it
  // navigates somewhere beyond just marking the row read. The chevron only
  // renders on these rows so users can tell informational rows from
  // navigational ones at a glance.
  const hasAction = (n: Notification): boolean => {
    if (n.id.startsWith(ANNOUNCEMENT_ID_PREFIX)) return !!link(n);
    if (
      (n.kind === 'template_draft_completed' ||
        n.kind === 'template_draft_failed' ||
        n.kind === 'template_draft_cancelled') &&
      currentSlug
    ) {
      const meta = (n.metadata as { job_id?: string } | null) ?? null;
      if (meta?.job_id) return true;
    }
    if (n.kind === 'chat_message' && currentSlug) {
      const meta = (n.metadata as { conversation_id?: string } | null) ?? null;
      if (meta?.conversation_id) return true;
    }
    return !!link(n);
  };

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('notifications.openMenu', { count: unread })}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
        className="relative"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-pill bg-[var(--lime,#BEE034)] px-1 font-heading text-[0.6875rem] font-bold leading-none text-[var(--ink,#26272B)]"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={t('notifications.title')}
          className="absolute right-0 top-full z-40 mt-2 w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border bg-background shadow-xl"
        >
          <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h2 className="font-heading text-sm font-bold">
              {t('notifications.title')}
              {unread > 0 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {t('notifications.unreadCount', { count: unread })}
                </span>
              )}
            </h2>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => {
                  markAll.mutate();
                  markAllSeen();
                }}
                disabled={markAll.isPending}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </header>

          <div className="max-h-[28rem] overflow-y-auto">
            {query.isPending && items.length === 0 && (
              <p className="px-4 py-6 text-sm text-muted-foreground">
                {t('common.loading')}
              </p>
            )}
            {!query.isPending && items.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/50" weight="duotone" />
                <p className="text-sm text-muted-foreground">
                  {t('notifications.empty')}
                </p>
              </div>
            )}
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  dateFmt={dateFmt}
                  hasAction={hasAction(n)}
                  onClick={() => {
                    // Synthetic announcement row — mark seen in localStorage,
                    // then fall through to the action_url branch below if
                    // the entry has one. No server-side row to mark read.
                    if (n.id.startsWith(ANNOUNCEMENT_ID_PREFIX)) {
                      markSeen(n.id.slice(ANNOUNCEMENT_ID_PREFIX.length));
                      follow(link(n));
                      setOpen(false);
                      return;
                    }
                    if (!n.read_at) markRead.mutate([n.id]);
                    // Kind-specific deep links live here. template_draft_*
                    // routes back into the schedule page with a query
                    // param the dialog picks up to resume polling /
                    // render the preview. Everything else falls back to
                    // action_url if the backend set one.
                    if (
                      (n.kind === 'template_draft_completed' ||
                        n.kind === 'template_draft_failed' ||
                        n.kind === 'template_draft_cancelled') &&
                      currentSlug
                    ) {
                      const meta = (n.metadata as { job_id?: string } | null) ?? null;
                      if (meta?.job_id) {
                        navigate(
                          `/orgs/${currentSlug}/schedule?mode=template&resumeDraft=${encodeURIComponent(meta.job_id)}`,
                        );
                        setOpen(false);
                        return;
                      }
                    }
                    // Chat messages deep-link into the thread panel via the
                    // metadata conversation id — client-side navigate, and
                    // works for rows persisted before action_url carried the
                    // full /orgs/<slug>/... path.
                    if (n.kind === 'chat_message' && currentSlug) {
                      const meta =
                        (n.metadata as { conversation_id?: string } | null) ?? null;
                      if (meta?.conversation_id) {
                        navigate(
                          `/orgs/${currentSlug}/chat?conversation=${encodeURIComponent(meta.conversation_id)}`,
                        );
                        setOpen(false);
                        return;
                      }
                    }
                    // Everything else follows the resolved action_url. Closing
                    // the popover is explicit now that this is a client-side
                    // navigation rather than a full page load.
                    const target = link(n);
                    if (target) {
                      follow(target);
                      setOpen(false);
                    }
                  }}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  n,
  dateFmt,
  hasAction,
  onClick,
}: {
  n: Notification;
  dateFmt: Intl.DateTimeFormat;
  hasAction: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('common');
  const created = new Date(n.created_at);

  // Localised title — fall back to the server's pre-rendered title when no
  // template exists for this kind. Same pattern as the activity feed.
  const meta = (n.metadata ?? {}) as Record<string, unknown>;
  let title = t(`notifications.kind.${n.kind}.title`, {
    ...meta,
    defaultValue: n.title,
  });
  // A failed broadcast carries a typed reason code rather than a body string
  // (the server avoids stuffing a raw English error in). Resolve it to a
  // localized message keyed by that code, falling back to the generic one.
  let body: string;
  if (n.kind === 'chat_message') {
    // Title is the conversation name so the user knows which chat it is;
    // direct messages have no title, so the sender's name takes its place.
    // In a named (group) chat the body line carries the sender prefix.
    const convTitle =
      typeof meta.conversation_title === 'string' ? meta.conversation_title.trim() : '';
    const sender = typeof meta.sender_name === 'string' ? meta.sender_name : '';
    if (convTitle) {
      title = convTitle;
      body =
        sender && n.body
          ? t('notifications.kind.chat_message.bodyWithSender', {
              sender_name: sender,
              message: n.body,
            })
          : (n.body ?? '');
    } else {
      title = sender || title;
      body = n.body ?? '';
    }
  } else if (n.kind === 'broadcast_send_failed') {
    const reason = typeof meta.reason === 'string' ? meta.reason : 'unknown';
    body = t(`notifications.kind.broadcast_send_failed.reason.${reason}`, {
      ...meta,
      defaultValue: t('notifications.kind.broadcast_send_failed.reason.unknown', { ...meta }),
    });
  } else if (n.kind === 'feedback_updated') {
    // Webhook posts the GitHub action ("commented" / "closed" / "reopened")
    // as the body. Translate it via a per-event sub-key so the bell never
    // shows a raw English verb.
    const event = typeof meta.event === 'string' ? meta.event : '';
    body = t(`notifications.kind.feedback_updated.body.${event}`, {
      ...meta,
      defaultValue: t('notifications.kind.feedback_updated.body.unknown', { ...meta }),
    });
  } else if (n.body) {
    const translated = t(`notifications.kind.${n.kind}.body`, { ...meta, defaultValue: n.body });
    // If the template still has an unfilled {{placeholder}} — e.g. an older
    // notification whose metadata predates the variable — fall back to the
    // server's pre-rendered body instead of showing the raw "{{subject}}".
    body = translated.includes('{{') ? n.body : translated;
  } else {
    body = '';
  }

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
          !n.read_at && 'bg-[var(--color-accent-soft,rgba(190,224,52,0.08))]',
        )}
      >
        <SeverityIcon severity={n.severity} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {body && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{body}</p>
          )}
          <p className="mt-1 text-[0.6875rem] text-muted-foreground">
            {dateFmt.format(created)}
          </p>
        </div>
        {!n.read_at && (
          <span
            aria-label={t('notifications.unreadDot')}
            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--lime,#BEE034)]"
          />
        )}
        {hasAction && (
          <CaretRight
            aria-hidden
            weight="bold"
            className="mt-1 h-4 w-4 shrink-0 text-muted-foreground"
          />
        )}
      </button>
    </li>
  );
}

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  const map: Record<NotificationSeverity, { Icon: typeof Info; className: string }> = {
    info: { Icon: Info, className: 'text-foreground/60' },
    success: { Icon: CheckCircle, className: 'text-emerald-600 dark:text-emerald-400' },
    warning: { Icon: Warning, className: 'text-amber-500' },
    error: { Icon: XCircle, className: 'text-red-500' },
  };
  const { Icon, className } = map[severity];
  return (
    <Icon
      aria-hidden
      weight="duotone"
      className={cn('mt-0.5 h-5 w-5 shrink-0', className)}
    />
  );
}
